<template>
  <div>
    <b-modal
      :id="isBulkMode ? `mc-modal-bulk-transfer` :`mc-modal-transfer-${deliveryOrder?.id}-${pointId}`"
      ref="mc-modal-transfer"
      class="mc-modal"
      :title="selectedModalType.title"
      size="xl"
      centered
      scrollable
      no-close-on-esc
      no-close-on-backdrop
      @hide="handleCloseModal"
    >
      <!-- eslint-disable-next-line vue/require-component-is -->
      <component
        :is="selectedModalType.content"
        :deliveryOrderId="deliveryOrder?.id"
        :selectedType="selectedAction"
        :selectedFOId="selectedFO.id"
        :transferReasonFullObject="transferReasonFullObject"
        :transferPurposeType="transferPurposeType"
        :deliveryOrderIds="deliveryOrderIds"
        :selectedOrderIds="selectedOrderIds"
        @click-card="handleSelectAction"
        @update:newFleetTask="selectedFO = $event"
        @update:transferReasonFullObject="transferReasonFullObject = $event"
        @update:transferPurposeType="transferPurposeType = $event"
        @update:selectedOrderIds="selectedOrderIds = $event"
      >
        <template #alert>
          <McAlert
            v-if="hasPickedStatus"
            withIcon
            variant="warning"
            class="mb-2"
            :text="$t('fleetTask.transferDeliveryModal.alert.deletePickupProof')"
          />
        </template>
      </component>

      <template #modal-footer="{ cancel }">
        <div class="d-flex mx-0">
          <McButton
            variant="secondary"
            class="me-2"
            :text="selectedModalType.cancelText"
            @click="() => handleClickSecondaryButton(cancel)"
          />
          <McButton
            variant="primary"
            :disabled="disableOkText"
            :text="selectedModalType.okText"
            @click="handleClickPrimaryButton"
          />
        </div>
      </template>
    </b-modal>

    <ModalConfirmSummaryTemperature
      :data="dataConfirmTemp"
      :modal-id="isBulkMode ? `modal-confirm-summary-temperature-bulk-transfer` : `modal-confirm-summary-temperature-transfer-${deliveryOrder?.id}`"
      @handleSubmit="handleApplyTransferDO"
      @handleCancel="handleCancelTransferDO"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { isEmpty } from 'lodash'

import McButton from '@/components/McButton'
import McWizard from '@/components/McWizard'
import McAlert from '@/components/McAlert'
import ModalConfirmSummaryTemperature from '../../components/Modal/ConfirmSummaryTemperature.vue'

import InitialContent from './contents/initial.vue'
import SelectContent from './contents/select.vue'
import ReviewContent from './contents/review.vue'
import selectDeliveryOrders from './contents/select-delivery-orders'

import { getWizardItems } from './lib/constants/wizard-items.js'
import getRelatedActivity from '../../lib/get-related-activity'

import * as FleetTaskDeliveryOrder from '@/services/rest-api/lib/fleet-task/delivery-order'

import { errorCodeCheckedMap } from '../../lib/constants/error-code-mappings'

export default {
  components: {
    McButton,
    McWizard,
    ModalConfirmSummaryTemperature,
    McAlert,
  },
  props: {
    deliveryOrder: {
      type: Object,
      default: null,
    },
    pointId: {
      type: String,
    },
    isBulkMode: {
      type: Boolean,
      default: false,
    },
    deliveryOrderIds: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      modalType: this.isBulkMode ? "select-delivery-orders" : "select",
      selectedAction: 'exist',
      selectedFO: {},
      isSubmitting: false,
      
      dataConfirmTemp: {},
      skipCheckTemp: false,
      dontShowAgain: false,
      
      selectedOrderIds: {},
      transferReasonFullObject: {},
      transferPurposeType: 'exist',
    }
  },
  computed: {
    ...mapState('fleetTask', [
      'sequence',
      'selectedDos',
    ]),
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      selectedFOTransfer: 'fleetTask/getSelectedFOTransfer',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    selectedFOEffective() {
      return this.selectedFO.id ? this.selectedFO : this.selectedFOTransfer || {}
    },
    selectedModalType() {
      return this.modalTypeMapping[this.modalType]
    },
    wizardItems() {
      return getWizardItems(this.$t.bind(this))
    },
    disableOkText() {
      if (this.modalType === 'select-delivery-orders') {
        return Object.entries(this.selectedOrderIds).filter(Boolean).length <= 0
      }

      if (this.modalType == 'select') {
        return (
          (this.transferPurposeType === 'exist' && !this.selectedFOEffective.id) ||
          isEmpty(this.transferReasonFullObject)
        )
      }

      return this.isSubmitting
    },
    hasPickedStatus() {
      // Check single delivery order (non-bulk mode)
      if (this.deliveryOrder?.id) {
        const relatedActivity = getRelatedActivity(
          { delivery_order: { id: this.deliveryOrder.id } },
          this.sequence
        )
        if (relatedActivity?.status?.raw_type === 'COMPLETED') {
          return true
        }
      }
      
      // Check multiple delivery orders (bulk mode)
      const selectedIds = Object.keys(this.selectedOrderIds).filter(
        key => this.selectedOrderIds[key] === true
      )
      
      for (const orderId of selectedIds) {
        const relatedActivity = getRelatedActivity(
          { delivery_order: { id: orderId } },
          this.sequence
        )
        if (relatedActivity?.status?.raw_type === 'COMPLETED') {
          return true
        }
      }

      return false
    },
    
    modalTypeMapping() {
      return {
        initial: {
          key: 'initial',
          cancelText: this.$t('fleetTask.transferDeliveryModal.action.cancel'),
          okText: this.$t('fleetTask.transferDeliveryModal.action.next'),
          content: InitialContent,
        },
        "select-delivery-orders": {
          title: this.$t('fleetTask.transferDeliveryModal.title.selectDelivery'),
          key: 'select-delivery-orders',
          cancelText: this.$t('fleetTask.transferDeliveryModal.action.cancel'),
          okText: this.$t('fleetTask.transferDeliveryModal.action.next'),
          content: selectDeliveryOrders,
        },
        select: {
          title: this.$t('fleetTask.transferDeliveryModal.title.selectDestinationFleetTask'),
          key: 'select',
          cancelText: this.isBulkMode
            ? this.$t('fleetTask.transferDeliveryModal.action.previous')
            : this.$t('fleetTask.transferDeliveryModal.action.cancel'),
          okText: this.transferPurposeType === 'exist'
            ? this.$t('fleetTask.transferDeliveryModal.action.next')
            : this.$t('fleetTask.transferDeliveryModal.action.transfer'),
          content: SelectContent,
        },
        review: {
          title: this.$t('fleetTask.transferDeliveryModal.title.reviewTransferDelivery'),
          cancelText: this.$t('fleetTask.transferDeliveryModal.action.previous'),
          okText: this.$t('fleetTask.transferDeliveryModal.action.transfer'),
          content: ReviewContent,
        },
      }
    },
  },
  methods: {
    onModalShown() {
      const saved = this.$store.getters['fleetTask/getSelectedFOTransfer']
      if (saved && !this.selectedFO.id) {
        this.selectedFO = saved
      }
    },
    handleSelectAction(type) {
      this.selectedAction = type
    },
    async handleSubmitTransfer() {
      try {
        await this.$store.dispatch('fleetTask/setIsLoadingGlobalDelay', true)
        this.$store.dispatch('fleetTask/setIsInitial', false)
        this.isSubmitting = true
  
        const data = {
          type: this.transferPurposeType,
          fleet_task_uuid: this.selectedFO.id,
          transferred_reason_uuid: this.transferReasonFullObject.id,
          ...(this.isBulkMode ? {
            delivery_order_uuids: Object.keys(this.selectedOrderIds).filter((key) => this.selectedOrderIds[key] === true)
          } : 
          {}),
        }
        
        if (this.transferPurposeType === 'exist') {
          const savedPref = localStorage.getItem('hideTemperatureSummary')
    
          if (savedPref) {
            this.dontShowAgain = JSON.parse(savedPref)
          }
    
          if (
            this.hasAccessAll(['tms-cold-chain:read']) &&
            !this.skipCheckTemp &&
            !this.dontShowAgain
          ) {
            const canProceed = await this.checkComparationTemp()
            if (!canProceed) {
              this.isSubmitting = false
              return
            }
          }
        }
  
        let res

        if (this.isBulkMode) {
          res = await FleetTaskDeliveryOrder.bulkTransfer(
            this.$route.params.id,
            data
          )
          
          if (res.errors) {
            this.$mcToast.error({
              component: 'McToastDynamicBody',
              props: {
                contents: [
                  {
                    tag: 'b',
                    text: Object.keys(this.selectedOrderIds).length,
                  },
                  {
                    tag: 'span',
                    text: this.$t('fleetTask.transferDeliveryModal.toast.deliveryFailedMovedTo'),
                  },
                  {
                    tag: 'b',
                    text: this.transferPurposeType === 'exist'
                      ? this.selectedFO.number
                      : this.$t('fleetTask.transferDeliveryModal.common.newFleetTask'),
                  },
                ],
              },
            })
    
            await this.handleClearModalTransferDO()
            return
          }
          
          if (res.data.delivery_order?.succeeded_uuids?.length > 0) {           
            this.$mcToast.success({
              component: 'McToastDynamicBody',
              props: {
                contents: [
                  {
                    tag: 'b',
                    text: `${res.data.delivery_order.succeeded_uuids.length}`,
                  },
                  {
                    tag: 'span',
                    text: this.$t('fleetTask.transferDeliveryModal.toast.deliverySuccessMovedTo'),
                  },
                  {
                    tag: 'b',
                    text: res.data.fleet_task.number,
                  },
                ],
              },
            })

            if (this.transferPurposeType === 'create') {
              this.$mcToast.success({
                component: 'McToastDynamicBody',
                props: {
                  contents: [
                    {
                      tag: 'b',
                      text: res.data.fleet_task.number,
                    },
                    {
                      tag: 'span',
                      text: this.$t('fleetTask.transferDeliveryModal.toast.savedAsDraft'),
                    },
                  ],
                },
              })
            }
          }

          if (res.data.delivery_order?.failed_uuids?.length > 0) {
            this.$mcToast.error({
              component: 'McToastDynamicBody',
              props: {
                contents: [
                  {
                    tag: 'b',
                    text: res.data.delivery_order.succeeded_uuids.length,
                  },
                  {
                    tag: 'span',
                    text: this.$t('fleetTask.transferDeliveryModal.toast.deliveryFailedMovedTo'),
                  },
                  {
                    tag: 'b',
                    text: res.data.fleet_task.number,
                  },
                ],
              },
            })

            await this.handleClearModalTransferDO()
            return
          }
        } else {
          res = await FleetTaskDeliveryOrder.transfer(
            this.$route.params.id,
            this.deliveryOrder.id,
            data
          )

          if (res.errors) {
            const issueCodes = res.errors_v2.issues.map((issue) => issue.code)
            const hasIssue = errorCodeCheckedMap[issueCodes[0]]?.checked
            const contents = [
              {
                tag: 'b',
                text: this.deliveryOrder.number,
              },
              {
                tag: 'span',
                text: this.$t('fleetTask.transferDeliveryModal.toast.deliveryFailedMovedTo'),
              },
              {
                tag: 'b',
                text: this.transferPurposeType === 'exist'
                  ? this.selectedFO.number
                  : this.$t('fleetTask.transferDeliveryModal.common.newFleetTaskWithPeriod'),
              },
            ]

            if (hasIssue) {
              contents.push({
                tag: 'span',
                text: errorCodeCheckedMap[issueCodes[0]]?.message || '',
              })
            }

            this.$mcToast.error({
              component: 'McToastDynamicBody',
              props: {
                contents,
              },
            })
            
            await this.handleClearModalTransferDO()
            return
          }

          this.$mcToast.success({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  tag: 'b',
                  text: this.deliveryOrder.number,
                },
                {
                  tag: 'span',
                  text: this.$t('fleetTask.transferDeliveryModal.toast.deliverySuccessMovedToSingle'),
                },
                {
                  tag: 'b',
                  text: res.data.fleet_task.number,
                },
              ],
            },
          })

          if (this.transferPurposeType === 'create') {
            this.$mcToast.success({
              component: 'McToastDynamicBody',
              props: {
                contents: [
                  {
                    tag: 'b',
                    text: res.data.fleet_task.number,
                  },
                  {
                    tag: 'span',
                    text: this.$t('fleetTask.transferDeliveryModal.toast.savedAsDraft'),
                  },
                ],
              },
            })
          }
        }

        if (!res.data.fleet_task?.uuid) {
          this.$mcToast.error(this.$t('fleetTask.transferDeliveryModal.toast.fleetTaskDataUnavailable'))
          await this.handleClearModalTransferDO()
          return
        }
  
        this.$router.push({
          name: 'Fleet Task Detail',
          params: { id: res.data.fleet_task?.uuid },
          query: { ...this.$route.query },
        })
        this.handleClearModalTransferDO()
      } catch (e) {
        this.$mcToast.error(this.$t('fleetTask.transferDeliveryModal.toast.transferDeliveryFailed'))
        await this.handleClearModalTransferDO()
      }
    },
    handleClickSecondaryButton(cancel) {
      if (this.modalType == 'review') {
        this.modalType = 'select'
        return
      }
      
      if (this.modalType === 'select' && this.isBulkMode) {
        this.modalType = 'select-delivery-orders'
        this.handleClearSelectModalType()
        return
      }

      if (this.modalType == 'select' && !this.isBulkMode) {
        this.handleClearSelectModalType()
        this.$refs['mc-modal-transfer'].hide()
        return
      }

      this.modalType = this.isBulkMode ? 'select-delivery-orders' : 'select'
      this.selectedOrderIds = {}

      cancel()
    },
    async handleClickPrimaryButton() {
      if (this.modalType == 'review') {
        await this.handleSubmitTransfer()
        this.modalType = this.isBulkMode ? 'select-delivery-orders' : 'select'

        this.$refs['mc-modal-transfer'].hide()
        return
      }

      if (this.modalType === 'select-delivery-orders') {
        this.modalType = 'select'
        return
      }

      if (this.modalType == 'select') {
        if (this.transferPurposeType === 'create') {
          await this.handleSubmitTransfer()
          this.$refs['mc-modal-transfer'].hide()
          this.modalType = this.isBulkMode ? 'select-delivery-orders' : 'select'
          return
        }

        this.modalType = 'review'
        return
      }

      if (this.modalType == 'initial') {
        // TODO: Create logic for transfer DO to new FO
        if (this.selectedAction == 'create') {
          return
        }

        if (this.selectedAction == 'exist') {
          this.modalType = 'select'
        }
      }
    },
    async checkComparationTemp() {
      const res = await FleetTaskDeliveryOrder.get(this.selectedFOEffective.id)
      const selectedOrderItems = this.selectedDos.filter((item) => Object.keys(this.selectedOrderIds).includes(item.id))

      const joinDO = [
        ...res.data.map((item) => ({
          id: item.id,
          number: item.number,
          load_min_temperature: item.load_min_temperature,
          load_max_temperature: item.load_max_temperature,
        })),
        ...(
          this.deliveryOrder !== null ? [{
            id: this.deliveryOrder.id,
            number: this.deliveryOrder.number,
            load_min_temperature: this.deliveryOrder.min_temperature,
            load_max_temperature: this.deliveryOrder.max_temperature,
          }] : []
        ),
        ...(selectedOrderItems.length > 0 ? selectedOrderItems.map((item) => {
            return {
              id: item.id,
              number: item.number,
              load_min_temperature: item.load_min_temperature,
              load_max_temperature: item.load_max_temperature,
            }
          }) : []
        )
      ]
      const getTemp = this.getExtremeTemps(joinDO)

      if (getTemp.min !== null && getTemp.max !== null) {
        this.dataConfirmTemp = getTemp

        this.$store.dispatch('fleetTask/setSelectedFOTransfer', this.selectedFO)

        this.$bvModal.show(this.isBulkMode ? `modal-confirm-summary-temperature-bulk-transfer` : `modal-confirm-summary-temperature-transfer-${this.deliveryOrder?.id}`)

        return false
      }

      return true
    },
    getExtremeTemps(selectedItems) {
      if (!selectedItems || selectedItems.length < 2) {
        return { min: null, max: null, difference: null }
      }

      const withMin = selectedItems.filter(
        (i) => i.load_min_temperature !== null && i.load_min_temperature !== undefined
      )
      const withMax = selectedItems.filter(
        (i) => i.load_max_temperature !== null && i.load_max_temperature !== undefined
      )

      const minItem = withMin.length
        ? withMin.reduce((a, b) => (a.load_min_temperature < b.load_min_temperature ? a : b))
        : null

      const maxItem = withMax.length
        ? withMax.reduce((a, b) => (a.load_max_temperature > b.load_max_temperature ? a : b))
        : null

      const difference =
        minItem !== null && maxItem !== null
          ? Number((maxItem.load_max_temperature - minItem.load_min_temperature).toFixed(3))
          : null

      const allSame =
        new Set(withMin.map((i) => i.load_min_temperature)).size === 1 &&
        new Set(withMax.map((i) => i.load_max_temperature)).size === 1

      if (allSame) {
        return { min: null, max: null, difference: null }
      }

      return {
        min: minItem
          ? {
              id: minItem.id,
              number: minItem.number,
              load_min_temperature: minItem.load_min_temperature,
            }
          : null,
        max: maxItem
          ? {
              id: maxItem.id,
              number: maxItem.number,
              load_max_temperature: maxItem.load_max_temperature,
            }
          : null,
        difference,
      }
    },
    async handleApplyTransferDO() {
      this.$store.dispatch('fleetTask/setSelectedFOTransfer', this.selectedFO)
      this.skipCheckTemp = true
      await this.handleSubmitTransfer()
    },
    async handleCancelTransferDO() {
      await this.handleClearModalTransferDO()
    },
    handleCloseModal(bvModalEvent) {
      // bvModalEvent.trigger can be: 'backdrop', 'esc', 'headerclose', 'ok', 'cancel', 'event' or undefined (programmatic)
      if (bvModalEvent?.trigger === 'headerclose') {
        this.modalType = this.isBulkMode ? 'select-delivery-orders' : 'select'
        this.selectedOrderIds = {}
        this.handleClearSelectModalType()
      }
    },
    handleClearSelectModalType() {
      this.selectedFO = {}
      this.transferReasonFullObject = {}
      this.transferPurposeType = 'exist'
    },
    async handleClearModalTransferDO() {
      this.modalType = this.isBulkMode ? 'select-delivery-orders' : 'select'
      this.selectedOrderIds = {}
      this.handleClearSelectModalType()
      this.isSubmitting = false
      this.skipCheckTemp = false
      this.$refs['mc-modal-transfer'].hide()
      await this.$store.dispatch('fleetTask/setIsLoadingGlobalDelay', false)
    },
  },
}
</script>
<style lang="scss" scoped>
:deep(.modal-header) {
  position: relative;
  
  .close {
    bottom: 0;
    width: unset;
    height: unset;
    opacity: 1;
  }
}
</style>
